/**
 * Content generators for individual entity action files.
 * Each function returns the string content for one generated file.
 */

import type { SchemaField } from '@core-engine/schema/types.js'
import { singularize, toCamelCase, toKebabCase, toPascalCase } from '@core-engine/utils/string.js'
import {
  buildAuthorshipAliasBlock,
  type ReadSelectField
} from '@next-adapter/generators/actions/action-helpers.js'
import {
  afterHook,
  beforeHook,
  lifecycleContext,
  lifecycleHookImport
} from '@next-adapter/generators/actions/lifecycle-hook-emissions.js'
import type { ResolvedEntityFilter } from '@next-adapter/generators/entity-filters.js'

/** Shared context for all entity file generators */
export interface EntityCtx {
  tableName: string
  singular: string
  plural: string
  Singular: string
  Plural: string
  tableVar: string
  camelSingular: string
  camelPlural: string
  cacheTag: string
  cacheReadImport: string
  cacheLifeLine: string
  /** Cache invalidation function name: 'updateTag' (Next >=16) or 'revalidateTag' (Next <16) */
  cacheInvalidationFn: string
  hasRelationships: boolean
  hasM2M: boolean
  hasListRels: boolean
  hasMediaFields: boolean
  mediaFields: SchemaField[]
  galleryFields: SchemaField[]
  hasHtmlOutput: boolean
  hasSlug: boolean
  slugSourceField?: string
  hasDraft: boolean
  hasSearch: boolean
  hasFilters: boolean
  hasSelectFields: boolean
  hasCreatableSelectFields: boolean
  searchFields: string[]
  htmlOutputFields: SchemaField[]
  selectFields: ReadSelectField[]
  creatableSelectFields: SchemaField[]
  m2mFields: SchemaField[]
  filterableFields: SchemaField[]
  relTableImports: string[]
  listRelTableImports: string[]
  // Pre-computed template fragments
  dataInterface: string
  responseInterface: string
  filtersInterface: string
  createInterface: string
  updateInterface: string
  selectClause: string
  listSelectClause: string
  filterConditions: string
  searchBlock: string
  resultMapping: string
  listResultMapping: string
  singleRowReturn: string
  fieldMeta: string
  createMappings: string
  htmlCreateBlock: string
  htmlCreateMappings: string
  populateListRelsFn: string
  filters: ResolvedEntityFilter[]
  sortableColumns: string[]
  selectOptionsTableVar: string
  versionSnapshotKeys: string[]
  versionRestoreKeys: string[]
}

export interface SelectOptionCtx {
  singular: string
  plural: string
  Singular: string
  camelSingular: string
  cacheReadImport: string
  cacheLifeLine: string
  /** Cache invalidation function name: 'updateTag' (Next >=16) or 'revalidateTag' (Next <16) */
  cacheInvalidationFn: string
  creatableSelectFields: SchemaField[]
  selectOptionsTableVar: string
}

// ============================================================================
// types.ts
// ============================================================================

export function genTypesContent(ctx: EntityCtx): string {
  const idPlaceholder = '$' + '{id}'
  const tagEntries = [
    `  all: '${ctx.cacheTag}'`,
    `  byId: (id: string) => \`${ctx.plural}:id:${idPlaceholder}\``
  ]
  if (ctx.hasSlug) {
    tagEntries.push(`  bySlug: (slug: string) => \`${ctx.plural}:slug:\${slug}\``)
  }

  const parts = [
    ctx.dataInterface,
    ctx.responseInterface,
    ctx.filtersInterface,
    ctx.hasCreatableSelectFields ? buildSelectOptionTypes(ctx) : null,
    ctx.createInterface,
    `export interface ${ctx.Singular}CreateResult {\n  success: boolean\n  error?: string\n  ${ctx.camelSingular}?: ${ctx.Singular}\n}`,
    ctx.updateInterface,
    `export interface ${ctx.Singular}UpdateResult {\n  success: boolean\n  error?: string\n  ${ctx.camelSingular}?: ${ctx.Singular}\n}`,
    `export interface ${ctx.Singular}DeleteResult {\n  success: boolean\n  error?: string\n}`,
    `export const ${ctx.camelPlural}CacheTags = {\n${tagEntries.join(',\n')}\n} as const`
  ]
  return `${parts.filter(Boolean).join('\n\n')}\n`
}

export function buildSelectOptionTypes(ctx: SelectOptionCtx): string {
  const fieldNames = ctx.creatableSelectFields
    .map((field) => JSON.stringify(field.name))
    .join(' | ')

  return `export type ${ctx.Singular}SelectOptionFieldName = ${fieldNames}

export interface ${ctx.Singular}SelectOption {
  id: string
  fieldName: ${ctx.Singular}SelectOptionFieldName
  label: string
  value: string
  createdAt: string
  updatedAt: string
}

export interface Create${ctx.Singular}SelectOptionInput {
  fieldName: ${ctx.Singular}SelectOptionFieldName
  label: string
}

export interface Create${ctx.Singular}SelectOptionResult {
  success: boolean
  error?: string
  option?: ${ctx.Singular}SelectOption
}

export const ${ctx.camelSingular}SelectOptionsCacheTags = {
  byField: (fieldName: ${ctx.Singular}SelectOptionFieldName) => \`${ctx.plural}:select-options:\${fieldName}\`
} as const`
}

// ============================================================================
// helpers.ts (conditional)
// ============================================================================

export function genHelpersContent(ctx: EntityCtx): string | null {
  const lines: string[] = []
  const drizzleImports = new Set<string>()
  const schemaImports = new Set<string>()

  if (ctx.hasM2M) {
    drizzleImports.add('eq')
    for (const field of ctx.m2mFields) {
      schemaImports.add(toCamelCase(`${ctx.singular}${toPascalCase(field.relationship || '')}`))
    }
  }
  if (ctx.hasListRels) {
    drizzleImports.add('inArray')
    for (const t of ctx.listRelTableImports) schemaImports.add(t)
  }
  if (ctx.hasMediaFields) {
    drizzleImports.add('inArray')
    schemaImports.add('adminMedia')
  }
  if (ctx.hasCreatableSelectFields) {
    schemaImports.add(ctx.selectOptionsTableVar)
  }
  if (ctx.hasSlug) {
    schemaImports.add(ctx.tableVar)
  }

  if (
    ctx.hasM2M ||
    ctx.hasListRels ||
    ctx.hasMediaFields ||
    ctx.hasCreatableSelectFields ||
    ctx.hasSlug
  ) {
    lines.push(`import db from '@admin/db'`)
  }
  const tables = [...schemaImports].sort()
  if (tables.length > 0) {
    lines.push(`import { ${tables.join(', ')} } from '@admin/db/schema'`)
  }
  if (drizzleImports.size > 0) {
    lines.push(`import { ${[...drizzleImports].sort().join(', ')} } from 'drizzle-orm'`)
  }
  if (ctx.hasMediaFields) {
    lines.push(`import type { AdminMedia } from '@admin/types'`)
  }
  if (ctx.hasSlug) {
    lines.push(`import { slugify } from '@admin/utils/validation/validation'`)
  }
  const typeImports = ctx.hasSelectFields ? `${ctx.Singular}, SelectOptionValue` : ctx.Singular
  lines.push(`import type { ${typeImports} } from './types'`)
  lines.push('')
  lines.push(`export function formatActionDate(value: unknown): string {
  if (typeof value !== 'string') return ''
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ''

  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: '2-digit',
    year: 'numeric',
    timeZone: 'UTC'
  })
}`)
  lines.push('')

  if (ctx.hasSlug) {
    lines.push(generateSlugHelpers(ctx))
    lines.push('')
  }

  if (ctx.hasListRels) {
    const exportedFn = ctx.populateListRelsFn
      .replace('async function', 'export async function')
      .trim()
    lines.push(exportedFn)
    lines.push('')
  }

  if (ctx.hasMediaFields) {
    lines.push(generateResolveMediaFunction(ctx))
    lines.push('')
  }

  if (ctx.hasSelectFields) {
    lines.push(generateResolveSelectFieldsFunction(ctx))
    lines.push('')
  }

  lines.push(generateVersionSnapshotHelpers(ctx))
  lines.push('')

  return lines.join('\n')
}

function generateSlugHelpers(ctx: EntityCtx): string {
  const constraintName = `${ctx.tableName}_slug_key`

  return `const SLUG_CONSTRAINT_NAME = ${JSON.stringify(constraintName)}
const SLUG_RETRY_LIMIT = 5

type SlugLookupClient = typeof db | Parameters<Parameters<typeof db.transaction>[0]>[0]

interface Resolve${ctx.Singular}SlugOptions {
  requestedSlug?: unknown
  sourceValue?: unknown
  fallbackPrefix: string
  currentId?: string
}

function toSlugBase(value: unknown): string {
  if (typeof value !== 'string') return ''
  return slugify(value)
}

export function get${ctx.Singular}SlugRetryLimit(): number {
  return SLUG_RETRY_LIMIT
}

export function is${ctx.Singular}SlugConflict(error: unknown): boolean {
  if (typeof error !== 'object' || error === null) return false
  const record = error as Record<string, unknown>
  if (record.code !== '23505') return false
  const constraint = typeof record.constraint === 'string' ? record.constraint : ''
  const message = typeof record.message === 'string' ? record.message : ''
  return constraint === SLUG_CONSTRAINT_NAME || message.includes(SLUG_CONSTRAINT_NAME)
}

export async function resolveUnique${ctx.Singular}Slug(
  client: SlugLookupClient,
  options: Resolve${ctx.Singular}SlugOptions
): Promise<string> {
  const requestedBase = toSlugBase(options.requestedSlug)
  const sourceBase = requestedBase ? '' : toSlugBase(options.sourceValue)
  const usesFallback = !requestedBase && !sourceBase
  const baseSlug = requestedBase || sourceBase || \`\${options.fallbackPrefix}-1\`
  const rows = await client
    .select({ id: ${ctx.tableVar}.id, slug: ${ctx.tableVar}.slug })
    .from(${ctx.tableVar})
  const usedSlugs = new Set(
    rows
      .filter((row) => row.id !== options.currentId)
      .map((row) => row.slug)
      .filter((slug): slug is string => typeof slug === 'string' && slug.length > 0)
  )

  if (!usedSlugs.has(baseSlug)) return baseSlug

  let suffix = 2
  let candidate = usesFallback
    ? \`\${options.fallbackPrefix}-\${suffix}\`
    : \`\${baseSlug}-\${suffix}\`
  while (usedSlugs.has(candidate)) {
    suffix += 1
    candidate = usesFallback
      ? \`\${options.fallbackPrefix}-\${suffix}\`
      : \`\${baseSlug}-\${suffix}\`
  }

  return candidate
}`
}

function generateVersionSnapshotHelpers(ctx: EntityCtx): string {
  const snapshotKeys = ctx.versionSnapshotKeys.map((key) => JSON.stringify(key)).join(', ')
  const restoreKeys = ctx.versionRestoreKeys.map((key) => JSON.stringify(key)).join(', ')
  const m2mKeys = ctx.m2mFields.map((field) => JSON.stringify(field.name)).join(', ')
  const loadM2MStatements = ctx.m2mFields
    .map((field) => {
      const rel = field.relationship || ''
      const RelPascal = toPascalCase(rel)
      const relSingular = singularize(rel)
      const junctionVar = toCamelCase(`${ctx.singular}${RelPascal}`)
      const entityIdCol = `${ctx.singular}Id`
      const relIdCol = `${relSingular}Id`

      return `  const ${field.name}Rows = await client
    .select({ ${relIdCol}: ${junctionVar}.${relIdCol} })
    .from(${junctionVar})
    .where(eq(${junctionVar}.${entityIdCol}, id))
  snapshot.${field.name} = ${field.name}Rows.map((row) => row.${relIdCol})`
    })
    .join('\n')
  const snapshotClientType = ctx.hasM2M
    ? `\ntype VersionSnapshotClient = typeof db | Parameters<Parameters<typeof db.transaction>[0]>[0]\n`
    : ''
  const relationshipSnapshotHelper = ctx.hasM2M
    ? `
export async function build${ctx.Singular}VersionSnapshotWithRelationships(
  row: Partial<${ctx.Singular}> | Record<string, unknown>,
  client: VersionSnapshotClient = db
): Promise<Record<string, unknown>> {
  const source = row as Record<string, unknown>
  const snapshot = build${ctx.Singular}VersionSnapshot(source)
  const id = typeof source.id === 'string' ? source.id : null

  if (!id) {
    for (const key of VERSION_M2M_KEYS) {
      snapshot[key] = Array.isArray(snapshot[key]) ? snapshot[key] : []
    }
    return snapshot
  }

${loadM2MStatements}

  return snapshot
}
`
    : ''

  return `${snapshotClientType}const VERSION_SNAPSHOT_KEYS = [${snapshotKeys}] as const
const VERSION_RESTORE_KEYS = [${restoreKeys}] as const
const VERSION_M2M_KEYS = [${m2mKeys}] as const

export function build${ctx.Singular}VersionSnapshot(row: Partial<${ctx.Singular}> | Record<string, unknown>): Record<string, unknown> {
  const source = row as unknown as Record<string, unknown>
  const snapshot: Record<string, unknown> = {}

  for (const key of VERSION_SNAPSHOT_KEYS) {
    if (key in source) {
      snapshot[key] = source[key]
    }
  }

  for (const key of VERSION_M2M_KEYS) {
    snapshot[key] = Array.isArray(snapshot[key]) ? snapshot[key] : []
  }

  return snapshot
}
${relationshipSnapshotHelper}

export function build${ctx.Singular}RestoreData(data: Record<string, unknown>): Record<string, unknown> {
  const restoreData: Record<string, unknown> = {}

  for (const key of VERSION_RESTORE_KEYS) {
    if (key in data) {
      restoreData[key] = data[key]
    }
  }

  return restoreData
}

export function has${ctx.Singular}VersionChanges(
  previous: Record<string, unknown>,
  next: Record<string, unknown>
): boolean {
  return JSON.stringify(previous) !== JSON.stringify(next)
}`
}

function generateResolveMediaFunction(ctx: EntityCtx): string {
  const singleFields = ctx.mediaFields.map((f) => f.name)
  const arrayFields = ctx.galleryFields.map((f) => f.name)

  const collectSingle = singleFields
    .map(
      (name) =>
        `    for (const row of rows) {\n      const val = row.${name}\n      if (val && typeof val === 'string' && !val.startsWith('http')) ids.add(val)\n    }`
    )
    .join('\n')
  const collectArray = arrayFields
    .map(
      (name) =>
        `    for (const row of rows) {\n      const arr = row.${name}\n      if (Array.isArray(arr)) for (const id of arr) { if (id && !id.startsWith('http')) ids.add(id) }\n    }`
    )
    .join('\n')

  const assignSingle = singleFields
    .map(
      (name) =>
        `    row.${name}Media = row.${name} && !row.${name}.startsWith('http')\n      ? (() => {\n          const media = mediaMap.get(row.${name})\n          if (!media) return null\n          return {\n            ...media,\n            createdAtFormatted: formatActionDate(media.createdAt),\n            updatedAtFormatted: formatActionDate(media.updatedAt)\n          }\n        })()\n      : null`
    )
    .join('\n')
  const assignArray = arrayFields
    .map(
      (name) =>
        `    row.${name}Media = Array.isArray(row.${name})\n      ? row.${name}\n          .map((id: string) => {\n            const media = mediaMap.get(id)\n            if (!media) return null\n            return {\n              ...media,\n              createdAtFormatted: formatActionDate(media.createdAt),\n              updatedAtFormatted: formatActionDate(media.updatedAt)\n            }\n          })\n          .filter(Boolean) as AdminMedia[]\n      : []`
    )
    .join('\n')

  return `export async function resolve${ctx.Singular}MediaFields(rows: ${ctx.Singular}[]): Promise<${ctx.Singular}[]> {
  if (rows.length === 0) return rows

  const ids = new Set<string>()
${collectSingle}${collectArray ? `\n${collectArray}` : ''}

  if (ids.size === 0) {
    for (const row of rows) {
${singleFields.map((name) => `      row.${name}Media = null`).join('\n')}${arrayFields.length > 0 ? `\n${arrayFields.map((name) => `      row.${name}Media = []`).join('\n')}` : ''}
    }
    return rows
  }

  const mediaRows = await db.select().from(adminMedia).where(inArray(adminMedia.id, [...ids]))
  const mediaMap = new Map<string, AdminMedia>(mediaRows.map(m => [m.id, m]))

  for (const row of rows) {
${assignSingle}${assignArray ? `\n${assignArray}` : ''}
  }

  return rows
}`
}

function generateResolveSelectFieldsFunction(ctx: EntityCtx): string {
  const staticLabelMaps = ctx.selectFields
    .map((readField) => {
      const key = readField.path.join('.')
      const entries = (readField.field.options ?? [])
        .map((option) => `${JSON.stringify(option.value)}: ${JSON.stringify(option.label)}`)
        .join(', ')
      return `  ${JSON.stringify(key)}: { ${entries} }`
    })
    .join(',\n')
  const dynamicRowsBlock = ctx.hasCreatableSelectFields
    ? `
  const dynamicRows = await db
    .select({
      fieldName: ${ctx.selectOptionsTableVar}.fieldName,
      label: ${ctx.selectOptionsTableVar}.label,
      value: ${ctx.selectOptionsTableVar}.value
    })
    .from(${ctx.selectOptionsTableVar})

  for (const row of dynamicRows) {
    labelMaps[row.fieldName] ??= {}
    labelMaps[row.fieldName][row.value] = row.label
  }
`
    : ''
  const assignments = ctx.selectFields
    .map((readField) => {
      const key = readField.path.join('.')
      return `    resolveSelectFieldPath(source, ${JSON.stringify(readField.path)}, labelMaps[${JSON.stringify(key)}] ?? {})`
    })
    .join('\n')

  return `const STATIC_SELECT_OPTION_LABELS = {
${staticLabelMaps}
} satisfies Record<string, Record<string, string>>

function cloneSelectOptionLabelMaps(): Record<string, Record<string, string>> {
  return Object.fromEntries(
    Object.entries(STATIC_SELECT_OPTION_LABELS).map(([fieldName, labels]) => [
      fieldName,
      { ...labels }
    ])
  )
}

function toSelectOptionValues(value: unknown, labels: Record<string, string>): SelectOptionValue[] {
  const values = Array.isArray(value) ? value : value ? [value] : []

  return values
    .filter((item): item is string => typeof item === 'string' && item.length > 0)
    .map((item) => ({
      label: labels[item] ?? item,
      value: item
    }))
}

function resolveSelectFieldPath(
  source: Record<string, unknown>,
  path: readonly string[],
  labels: Record<string, string>
): void {
  const [fieldName, ...rest] = path
  if (!fieldName) return

  if (rest.length === 0) {
    source[fieldName] = toSelectOptionValues(source[fieldName], labels)
    return
  }

  const value = source[fieldName]
  if (Array.isArray(value)) {
    for (const item of value) {
      if (item && typeof item === 'object') {
        resolveSelectFieldPath(item as Record<string, unknown>, rest, labels)
      }
    }
    return
  }

  if (value && typeof value === 'object') {
    resolveSelectFieldPath(value as Record<string, unknown>, rest, labels)
  }
}

export async function resolve${ctx.Singular}SelectFields(rows: ${ctx.Singular}[]): Promise<${ctx.Singular}[]> {
  if (rows.length === 0) return rows

  const labelMaps = cloneSelectOptionLabelMaps()
${dynamicRowsBlock}
  for (const row of rows) {
    const source = row as unknown as Record<string, unknown>
${assignments}
  }

  return rows
}`
}

// ============================================================================
// get-{plural}.ts
// ============================================================================

export function genGetPluralContent(ctx: EntityCtx): string {
  const dbImports = [ctx.tableVar, 'user', ...ctx.relTableImports].sort()
  const drizzle: string[] = ['asc', 'desc']
  const hasDateRangeFilters = ctx.filters.some((filter) => filter.type === 'date-range')
  if (ctx.hasSearch || ctx.filterableFields.length > 1) drizzle.push('and')
  if (ctx.hasSearch) drizzle.push('ilike', 'or')
  if (ctx.filterableFields.some(() => true)) drizzle.push('eq')
  if (hasDateRangeFilters) drizzle.push('gte', 'lt')
  drizzle.push('sql')
  if (ctx.hasRelationships) drizzle.push('eq')
  const sortedDrizzle = [...new Set(drizzle)].sort()

  const typeImports = [`${ctx.Plural}Page`, `${ctx.Plural}Query`, `${ctx.Singular}`]
  const helperImports: string[] = ['formatActionDate']
  if (ctx.hasSelectFields) helperImports.push(`resolve${ctx.Singular}SelectFields`)
  if (ctx.hasListRels) helperImports.push(`populate${ctx.Singular}ListRelationships`)
  if (ctx.hasMediaFields) helperImports.push(`resolve${ctx.Singular}MediaFields`)
  const populateImport =
    helperImports.length > 0 ? `\nimport { ${helperImports.join(', ')} } from './helpers'` : ''
  const dateRangeHelper = hasDateRangeFilters
    ? `

function getExclusiveDateUpperBound(value?: string): string | null {
  if (!value) return null

  const date = new Date(\`\${value}T00:00:00.000Z\`)
  if (Number.isNaN(date.getTime())) return null

  date.setUTCDate(date.getUTCDate() + 1)
  return date.toISOString().slice(0, 10)
}`
    : ''

  // Build the column map for dynamic orderBy
  const sortableColumns = ctx.sortableColumns ?? []
  const columnMapEntries = sortableColumns
    .map((col: string) => `    '${col}': ${ctx.tableVar}.${col}`)
    .join(',\n')

  return `'use server'

import db from '@admin/db'
import { ${dbImports.join(', ')} } from '@admin/db/schema'
import { ${sortedDrizzle.join(', ')} } from 'drizzle-orm'
import { alias } from 'drizzle-orm/pg-core'
${ctx.cacheReadImport}
import { ${ctx.camelPlural}CacheTags } from './types'
import type { ${typeImports.sort().join(', ')} } from './types'${populateImport}${dateRangeHelper}

${buildAuthorshipAliasBlock()}

const SORTABLE_COLUMNS = {
${columnMapEntries}
} as const

export async function get${ctx.Plural}(query?: ${ctx.Plural}Query): Promise<${ctx.Plural}Page> {
  'use cache'${ctx.cacheLifeLine}
  cacheTag(${ctx.camelPlural}CacheTags.all)

  try {
    const conditions = []
${ctx.searchBlock}${ctx.filterConditions}

    const baseQuery = ${ctx.listSelectClause}

    // Determine sort order: use requested column or default to sortOrder
    const sortColumn = query?.orderBy && query.orderBy in SORTABLE_COLUMNS
      ? SORTABLE_COLUMNS[query.orderBy as keyof typeof SORTABLE_COLUMNS]
      : ${ctx.tableVar}.sortOrder
    const sortFn = query?.orderDirection === 'desc' ? desc : asc
    const orderClause = sortFn(sortColumn)

    const orderedQuery = conditions.length > 0
      ? baseQuery.where(and(...conditions)).orderBy(orderClause)
      : baseQuery.orderBy(orderClause)

    const rawLimit = query?.limit
    const limit = typeof rawLimit === 'number'
      ? Number.isFinite(rawLimit)
        ? Math.max(1, rawLimit)
        : 1
      : undefined
    const rawOffset = query?.offset
    const offset = typeof rawOffset === 'number' && Number.isFinite(rawOffset)
      ? Math.max(0, rawOffset)
      : 0
    const isPaginated = typeof limit === 'number'
    let results: unknown[]
    if (isPaginated) {
      const paginatedQuery = offset > 0
        ? orderedQuery.limit(limit).offset(offset)
        : orderedQuery.limit(limit)
      results = await paginatedQuery
    } else {
      results = await orderedQuery
    }

    const total = isPaginated
      ? Number((await db.select({ count: sql\`count(*)\` }).from(${ctx.tableVar})${ctx.hasSearch || ctx.filterableFields.length > 0 ? '.where(conditions.length > 0 ? and(...conditions) : undefined)' : ''})[0]?.count ?? 0)
      : results.length${ctx.listResultMapping}
  } catch (error) {
    console.error('Error fetching ${ctx.plural}:', error)
    throw new Error('Failed to fetch ${ctx.plural}')
  }
}
`
}

// ============================================================================
// get-{singular}-by-id.ts
// ============================================================================

export function genGetByIdContent(ctx: EntityCtx): string {
  const dbImports = [ctx.tableVar, 'user', ...ctx.relTableImports].sort()
  const drizzle: string[] = ['eq']
  const sortedDrizzle = [...new Set(drizzle)].sort()

  const byIdHelperImports: string[] = ['formatActionDate']
  if (ctx.hasSelectFields) byIdHelperImports.push(`resolve${ctx.Singular}SelectFields`)
  if (ctx.hasListRels) byIdHelperImports.push(`populate${ctx.Singular}ListRelationships`)
  if (ctx.hasMediaFields) byIdHelperImports.push(`resolve${ctx.Singular}MediaFields`)
  const populateImport =
    byIdHelperImports.length > 0
      ? `\nimport { ${byIdHelperImports.join(', ')} } from './helpers'`
      : ''

  return `'use server'

import db from '@admin/db'
import { ${dbImports.join(', ')} } from '@admin/db/schema'
import { ${sortedDrizzle.join(', ')} } from 'drizzle-orm'
import { alias } from 'drizzle-orm/pg-core'
${ctx.cacheReadImport}
import { ${ctx.camelPlural}CacheTags } from './types'
import type { ${ctx.Singular} } from './types'${populateImport}

${buildAuthorshipAliasBlock()}

export async function get${ctx.Singular}ById(id: string): Promise<${ctx.Singular} | null> {
  'use cache'${ctx.cacheLifeLine}
  cacheTag(${ctx.camelPlural}CacheTags.byId(id))
  cacheTag(${ctx.camelPlural}CacheTags.all)

  try {
    const result = await ${ctx.selectClause}.where(eq(${ctx.tableVar}.id, id)).limit(1)
    if (result.length === 0) return null
    ${ctx.singleRowReturn}
  } catch (error) {
    console.error('Error fetching ${ctx.singular}:', error)
    throw new Error('Failed to fetch ${ctx.singular}')
  }
}
`
}

// ============================================================================
// get-{singular}-by-slug.ts (conditional)
// ============================================================================

export function genGetBySlugContent(ctx: EntityCtx): string | null {
  if (!ctx.hasSlug) return null
  const dbImports = [ctx.tableVar, 'user', ...ctx.relTableImports].sort()
  const drizzle: string[] = ['eq']
  const sortedDrizzle = [...new Set(drizzle)].sort()

  const bySlugHelperImports: string[] = ['formatActionDate']
  if (ctx.hasSelectFields) bySlugHelperImports.push(`resolve${ctx.Singular}SelectFields`)
  if (ctx.hasListRels) bySlugHelperImports.push(`populate${ctx.Singular}ListRelationships`)
  if (ctx.hasMediaFields) bySlugHelperImports.push(`resolve${ctx.Singular}MediaFields`)
  const populateImport =
    bySlugHelperImports.length > 0
      ? `\nimport { ${bySlugHelperImports.join(', ')} } from './helpers'`
      : ''

  return `'use server'

import db from '@admin/db'
import { ${dbImports.join(', ')} } from '@admin/db/schema'
import { ${sortedDrizzle.join(', ')} } from 'drizzle-orm'
import { alias } from 'drizzle-orm/pg-core'
${ctx.cacheReadImport}
import { ${ctx.camelPlural}CacheTags } from './types'
import type { ${ctx.Singular} } from './types'${populateImport}

${buildAuthorshipAliasBlock()}

export async function get${ctx.Singular}BySlug(slug: string): Promise<${ctx.Singular} | null> {
  'use cache'${ctx.cacheLifeLine}
  cacheTag(${ctx.camelPlural}CacheTags.bySlug(slug))
  cacheTag(${ctx.camelPlural}CacheTags.all)

  try {
    const result = await ${ctx.selectClause}.where(eq(${ctx.tableVar}.slug, slug)).limit(1)
    if (result.length === 0) return null
    ${ctx.singleRowReturn}
  } catch (error) {
    console.error('Error fetching ${ctx.singular} by slug:', error)
    throw new Error('Failed to fetch ${ctx.singular} by slug')
  }
}
`
}

// ============================================================================
// create-{singular}.ts
// ============================================================================

export function genCreateContent(ctx: EntityCtx): string {
  const kebabSingular = toKebabCase(ctx.singular)
  const slugSourceValue = ctx.slugSourceField ? `input.${ctx.slugSourceField}` : 'undefined'
  const snapshotImport = ctx.hasM2M
    ? `build${ctx.Singular}VersionSnapshotWithRelationships`
    : `build${ctx.Singular}VersionSnapshot`
  const snapshotData = ctx.hasM2M
    ? `await build${ctx.Singular}VersionSnapshotWithRelationships(createdRow as unknown as ${ctx.Singular}, tx)`
    : `build${ctx.Singular}VersionSnapshot(createdRow as unknown as ${ctx.Singular})`
  const markdownImport = ctx.hasHtmlOutput
    ? `\nimport { renderMarkdownSync } from './markdown'`
    : ''
  const createContext = lifecycleContext(
    ctx.plural,
    `,\n      input: input as unknown as Record<string, unknown>,\n      changedFields`
  )
  const createResultContext = lifecycleContext(
    ctx.plural,
    `,\n      entityId: created${ctx.Singular}Id,\n      input: input as unknown as Record<string, unknown>,\n      data: created${ctx.Singular}Data as unknown as Record<string, unknown>,\n      changedFields`
  )
  const createBeforePublishBlock = ctx.hasDraft
    ? `\n    const isCreatingPublished = input.published === true\n    if (isCreatingPublished) {\n      ${beforeHook('beforePublish', `{ ...lifecycleContext, changedFields: ['published'] }`)}\n    }\n`
    : ''
  const createAfterPublishBlock = ctx.hasDraft
    ? `\n    if (result[0]?.published === true) {\n      ${afterHook('afterPublish', `{ ...created${ctx.Singular}Context, changedFields: ['published'] }`)}\n    }`
    : ''

  return `'use server'

import db from '@admin/db'
import { requireRole, UserRole } from '@admin/auth/middleware'
import { ${ctx.tableVar} } from '@admin/db/schema'
import { desc } from 'drizzle-orm'
import { ${ctx.cacheInvalidationFn} } from 'next/cache'
import { createEntityVersion, entityVersionsCacheTags } from '@admin/actions/entity-versions'
import { ${ctx.camelPlural}CacheTags } from './types'
import { get${ctx.Singular}ById } from './get-${kebabSingular}-by-id'
import { getTouchedFieldNames } from '@admin/utils/audit/audit'
import {
  get${ctx.Singular}SlugRetryLimit,
  is${ctx.Singular}SlugConflict,
  resolveUnique${ctx.Singular}Slug,
  ${snapshotImport}
} from './helpers'
${lifecycleHookImport()}
import type { ${ctx.Singular}CreateInput, ${ctx.Singular}CreateResult, ${ctx.Singular} } from './types'${markdownImport}

export async function create${ctx.Singular}(input: ${ctx.Singular}CreateInput): Promise<${ctx.Singular}CreateResult> {
  const actor = await requireRole([UserRole.ADMIN, UserRole.EDITOR])

  try {
    const actorId = actor.id
    const now = new Date().toISOString()
    const auditRequestMetadata = await getAuditRequestMetadata()
    const changedFields = getTouchedFieldNames(input as unknown as Record<string, unknown>)
    const lifecycleContext = ${createContext}
    ${beforeHook('beforeCreate', 'lifecycleContext')}
${createBeforePublishBlock}

    let result: (typeof ${ctx.tableVar}.$inferSelect)[] | null = null
    for (let attempt = 0; attempt < get${ctx.Singular}SlugRetryLimit(); attempt += 1) {
      try {
        result = await db.transaction(async (tx) => {
          const maxSortOrderResult = await tx
            .select({ maxOrder: ${ctx.tableVar}.sortOrder })
            .from(${ctx.tableVar})
            .orderBy(desc(${ctx.tableVar}.sortOrder))
            .limit(1)
          const nextSortOrder = (maxSortOrderResult[0]?.maxOrder ?? 0) + 1
          const resolvedSlug = await resolveUnique${ctx.Singular}Slug(tx, {
            requestedSlug: input.slug,
            sourceValue: ${slugSourceValue},
            fallbackPrefix: '${toKebabCase(ctx.singular)}'
          })
${ctx.htmlCreateBlock}
          const createdRows = await tx.insert(${ctx.tableVar}).values({
${ctx.createMappings},${ctx.hasHtmlOutput ? `\n${ctx.htmlCreateMappings},` : ''}${ctx.hasDraft ? `\n      published: input.published ?? false,` : ''}
            sortOrder: nextSortOrder,
            createdAt: now,
            updatedAt: now,
            createdBy: actorId,
            updatedBy: actorId
          }).returning()

          const createdRow = createdRows[0]
          if (createdRow?.id) {
            await createEntityVersion(tx, {
              entityType: '${ctx.plural}',
              entityId: createdRow.id,
              data: ${snapshotData},
              createdBy: actorId
            })
          }

          return createdRows
        })
        break
      } catch (error) {
        if (is${ctx.Singular}SlugConflict(error) && attempt < get${ctx.Singular}SlugRetryLimit() - 1) {
          continue
        }
        throw error
      }
    }

    if (!result) throw new Error('Failed to create ${ctx.singular}')

    const created${ctx.Singular}Id = result[0]?.id
    ${ctx.cacheInvalidationFn}(${ctx.camelPlural}CacheTags.all)
    if (created${ctx.Singular}Id !== null && created${ctx.Singular}Id !== undefined) {
      ${ctx.cacheInvalidationFn}(${ctx.camelPlural}CacheTags.byId(created${ctx.Singular}Id))
      ${ctx.cacheInvalidationFn}(entityVersionsCacheTags.for('${ctx.plural}', created${ctx.Singular}Id))
    }
    const created${ctx.Singular} =
      typeof created${ctx.Singular}Id === 'string'
        ? await get${ctx.Singular}ById(created${ctx.Singular}Id)
        : null
    const created${ctx.Singular}Data = created${ctx.Singular} ?? (result[0] as unknown as ${ctx.Singular})
    const created${ctx.Singular}Context = ${createResultContext}
    ${afterHook('afterCreate', `created${ctx.Singular}Context`)}
${createAfterPublishBlock}

    return {
      success: true,
      ${ctx.camelSingular}: created${ctx.Singular}Data
    }
  } catch (error) {
    console.error('Error creating ${ctx.singular}:', error)
    throw new Error(error instanceof Error ? error.message : 'Failed to create ${ctx.singular}')
  }
}
`
}

// ============================================================================
// update-{singular}.ts
// ============================================================================

export function genUpdateContent(ctx: EntityCtx): string {
  const kebabSingular = toKebabCase(ctx.singular)
  const slugSourceValue = ctx.slugSourceField
    ? `updateData.${ctx.slugSourceField} ?? previousRow.${ctx.slugSourceField}`
    : 'undefined'
  const snapshotImport = ctx.hasM2M
    ? `build${ctx.Singular}VersionSnapshotWithRelationships`
    : `build${ctx.Singular}VersionSnapshot`
  const previousSnapshot = ctx.hasM2M
    ? `await build${ctx.Singular}VersionSnapshotWithRelationships(previousRow as unknown as ${ctx.Singular}, tx)`
    : `build${ctx.Singular}VersionSnapshot(previousRow as unknown as ${ctx.Singular})`
  const markdownImport = ctx.hasHtmlOutput
    ? `\nimport { renderMarkdownSync } from './markdown'`
    : ''
  const htmlUpdateBlock = ctx.hasHtmlOutput
    ? `\n` +
      ctx.htmlOutputFields
        .map(
          (f) =>
            `    if (processedData.${f.name} !== undefined) {\n      processedData.${f.name}Html = renderMarkdownSync(String(processedData.${f.name} || ''))\n    }`
        )
        .join('\n') +
      '\n'
    : ''
  const updateContext = lifecycleContext(
    ctx.plural,
    `,\n      entityId: id,\n      input: updateData as unknown as Record<string, unknown>`
  )
  const updateResultContext = lifecycleContext(
    ctx.plural,
    `,\n      entityId: id,\n      input: updateData as unknown as Record<string, unknown>,\n      data: updated${ctx.Singular}Data as unknown as Record<string, unknown>,\n      changedFields`
  )
  const updateBeforePublishBlock = ctx.hasDraft
    ? `\n    const isPublishing = previousRow.published !== true && updateData.published === true\n    const isUnpublishing = previousRow.published === true && updateData.published === false\n    if (isPublishing) {\n      ${beforeHook('beforePublish', 'lifecycleContext')}\n    }\n    if (isUnpublishing) {\n      ${beforeHook('beforeUnpublish', 'lifecycleContext')}\n    }\n`
    : ''
  const updateAfterPublishBlock = ctx.hasDraft
    ? `\n    if (isPublishing) {\n      ${afterHook('afterPublish', `{ ...updated${ctx.Singular}Context, changedFields: ['published'] }`)}\n    }\n    if (isUnpublishing) {\n      ${afterHook('afterUnpublish', `{ ...updated${ctx.Singular}Context, changedFields: ['published'] }`)}\n    }`
    : ''

  return `'use server'

import db from '@admin/db'
import { requireRole, UserRole } from '@admin/auth/middleware'
import { ${ctx.tableVar} } from '@admin/db/schema'
import { eq } from 'drizzle-orm'
import { ${ctx.cacheInvalidationFn} } from 'next/cache'
import { createEntityVersion, entityVersionsCacheTags } from '@admin/actions/entity-versions'
import { ${ctx.camelPlural}CacheTags } from './types'
import { get${ctx.Singular}ById } from './get-${kebabSingular}-by-id'
import { getChangedFieldNames } from '@admin/utils/audit/audit'
import {
  build${ctx.Singular}RestoreData,
  get${ctx.Singular}SlugRetryLimit,
  is${ctx.Singular}SlugConflict,
  resolveUnique${ctx.Singular}Slug,
  ${snapshotImport},
  has${ctx.Singular}VersionChanges
} from './helpers'
${lifecycleHookImport()}
import type { ${ctx.Singular}UpdateInput, ${ctx.Singular}UpdateResult, ${ctx.Singular} } from './types'${markdownImport}

export async function update${ctx.Singular}(input: ${ctx.Singular}UpdateInput): Promise<${ctx.Singular}UpdateResult> {
  const actor = await requireRole([UserRole.ADMIN, UserRole.EDITOR])

  try {
    const { id, ...updateData } = input
    const actorId = actor.id
    const now = new Date().toISOString()
    const auditRequestMetadata = await getAuditRequestMetadata()
    const [previousRow] = await db
      .select()
      .from(${ctx.tableVar})
      .where(eq(${ctx.tableVar}.id, id))
      .limit(1)

    if (!previousRow) throw new Error('${ctx.Singular} not found')

    const lifecycleContext = ${updateContext}
    ${beforeHook('beforeUpdate', 'lifecycleContext')}
${updateBeforePublishBlock}
    const requestedUpdateSlug = updateData.slug
    let changedFields: string[] = []
    let result: (typeof ${ctx.tableVar}.$inferSelect)[] | null = null
    for (let attempt = 0; attempt < get${ctx.Singular}SlugRetryLimit(); attempt += 1) {
      try {
        if (requestedUpdateSlug !== undefined) {
          updateData.slug = await resolveUnique${ctx.Singular}Slug(db, {
            requestedSlug: requestedUpdateSlug,
            sourceValue: ${slugSourceValue},
            fallbackPrefix: '${toKebabCase(ctx.singular)}',
            currentId: id
          })
        }

        const fieldMeta = [
        ${ctx.fieldMeta}
        ]

        const processedData: Record<string, unknown> = {}
        for (const [key, value] of Object.entries(updateData)) {
          if (key === 'published') { processedData[key] = value; continue }
          const field = fieldMeta.find(f => f.name === key)
          if (!field) continue
          if (field.type === 'list') {
            processedData[key] = value || []
          } else if (!field.required && ['number', 'decimal'].includes(field.type)) {
            processedData[key] =
              value === undefined ||
              value === null ||
              value === '' ||
              (typeof value === 'number' && Number.isNaN(value))
                ? null
                : value
          } else if (!field.required && ['date', 'timestamp', 'time', 'string', 'email', 'varchar', 'text', 'select', 'relationship'].includes(field.type)) {
            processedData[key] = value && value !== '' ? value : null
          } else {
            processedData[key] = value
          }
        }
${htmlUpdateBlock}
        changedFields = getChangedFieldNames(
          previousRow as unknown as Record<string, unknown>,
          processedData
        )
        result = await db.transaction(async (tx) => {
          const previousSnapshot = ${previousSnapshot}
          const nextSnapshot = {
            ...previousSnapshot,
            ...build${ctx.Singular}RestoreData(processedData)
          }
          const shouldCreateVersion = has${ctx.Singular}VersionChanges(previousSnapshot, nextSnapshot)

          if (shouldCreateVersion) {
            await createEntityVersion(tx, {
              entityType: '${ctx.plural}',
              entityId: id,
              data: previousSnapshot,
              createdBy: actorId
            })
          }

          return tx.update(${ctx.tableVar})
            .set({ ...processedData, updatedAt: now, updatedBy: actorId })
            .where(eq(${ctx.tableVar}.id, id))
            .returning()
        })
        break
      } catch (error) {
        if (is${ctx.Singular}SlugConflict(error) && attempt < get${ctx.Singular}SlugRetryLimit() - 1) {
          continue
        }
        throw error
      }
    }

    if (!result) throw new Error('${ctx.Singular} not found')

    if (result.length === 0) throw new Error('${ctx.Singular} not found')

    ${ctx.cacheInvalidationFn}(${ctx.camelPlural}CacheTags.all)
    ${ctx.cacheInvalidationFn}(${ctx.camelPlural}CacheTags.byId(id))
    ${ctx.cacheInvalidationFn}(entityVersionsCacheTags.for('${ctx.plural}', id))
    const updated${ctx.Singular} = await get${ctx.Singular}ById(id)
    const updated${ctx.Singular}Data = updated${ctx.Singular} ?? (result[0] as unknown as ${ctx.Singular})
    const updated${ctx.Singular}Context = ${updateResultContext}
${updateAfterPublishBlock}
    ${afterHook('afterUpdate', `updated${ctx.Singular}Context`)}

    return {
      success: true,
      ${ctx.camelSingular}: updated${ctx.Singular}Data
    }
  } catch (error) {
    console.error('Error updating ${ctx.singular}:', error)
    throw new Error(error instanceof Error ? error.message : 'Failed to update ${ctx.singular}')
  }
}
`
}

// ============================================================================
// get-versions-by-{singular}-id.ts / restore-{singular}-version.ts
// ============================================================================

export function genGetVersionsContent(ctx: EntityCtx): string {
  return `'use server'

import { getEntityVersions } from '@admin/actions/entity-versions'
import type { EntityVersionWithAuthor } from '@admin/actions/entity-versions'

export async function getVersionsBy${ctx.Singular}Id(
  id: string
): Promise<EntityVersionWithAuthor[]> {
  return getEntityVersions('${ctx.plural}', id)
}
`
}

function buildRestoreManyToManyStatements(ctx: EntityCtx): string {
  return ctx.m2mFields
    .map((field) => {
      const rel = field.relationship || ''
      const RelPascal = toPascalCase(rel)
      const relSingular = singularize(rel)
      const junctionVar = toCamelCase(`${ctx.singular}${RelPascal}`)
      const entityIdCol = `${ctx.singular}Id`
      const relIdCol = `${relSingular}Id`

      return `      const ${field.name}Value = versionData.${field.name}
      const ${field.name}Ids = Array.isArray(${field.name}Value)
        ? ${field.name}Value.filter((id): id is string => typeof id === 'string')
        : []
      await tx.delete(${junctionVar}).where(eq(${junctionVar}.${entityIdCol}, versionRow.entityId))
      if (${field.name}Ids.length > 0) {
        await tx.insert(${junctionVar}).values(
          ${field.name}Ids.map((${relSingular}Id) => ({
            ${entityIdCol}: versionRow.entityId,
            ${relIdCol}: ${relSingular}Id
          }))
        )
      }`
    })
    .join('\n')
}

export function genRestoreContent(ctx: EntityCtx): string {
  const kebabSingular = toKebabCase(ctx.singular)
  const snapshotImport = ctx.hasM2M
    ? `build${ctx.Singular}VersionSnapshotWithRelationships`
    : `build${ctx.Singular}VersionSnapshot`
  const currentSnapshot = ctx.hasM2M
    ? `await build${ctx.Singular}VersionSnapshotWithRelationships(currentRow as unknown as ${ctx.Singular}, tx)`
    : `build${ctx.Singular}VersionSnapshot(currentRow as unknown as ${ctx.Singular})`
  const dbImports = [
    'entityVersions',
    ctx.tableVar,
    ...ctx.m2mFields.map((field) => {
      const rel = field.relationship || ''
      return toCamelCase(`${ctx.singular}${toPascalCase(rel)}`)
    })
  ].sort()
  const restoreM2M = buildRestoreManyToManyStatements(ctx)

  return `'use server'

import db from '@admin/db'
import { requireRole, UserRole } from '@admin/auth/middleware'
import { ${dbImports.join(', ')} } from '@admin/db/schema'
import { and, eq } from 'drizzle-orm'
import { ${ctx.cacheInvalidationFn} } from 'next/cache'
import { createEntityVersion, entityVersionsCacheTags } from '@admin/actions/entity-versions'
import { ${ctx.camelPlural}CacheTags } from './types'
import { get${ctx.Singular}ById } from './get-${kebabSingular}-by-id'
import {
  build${ctx.Singular}RestoreData,
  ${snapshotImport}
} from './helpers'
import type { ${ctx.Singular}UpdateResult, ${ctx.Singular} } from './types'

export async function restore${ctx.Singular}Version(
  versionId: string
): Promise<${ctx.Singular}UpdateResult> {
  const actor = await requireRole([UserRole.ADMIN, UserRole.EDITOR])

  try {
    const actorId = actor.id
    const now = new Date().toISOString()

    const [versionRow] = await db
      .select()
      .from(entityVersions)
      .where(and(eq(entityVersions.id, versionId), eq(entityVersions.entityType, '${ctx.plural}')))
      .limit(1)

    if (!versionRow) throw new Error('${ctx.Singular} version not found')

    const versionData = versionRow.data as Record<string, unknown>
    const restoreData = build${ctx.Singular}RestoreData(versionData)

    const result = await db.transaction(async (tx) => {
      const [currentRow] = await tx
        .select()
        .from(${ctx.tableVar})
        .where(eq(${ctx.tableVar}.id, versionRow.entityId))
        .limit(1)

      if (!currentRow) throw new Error('${ctx.Singular} not found')

      await createEntityVersion(tx, {
        entityType: '${ctx.plural}',
        entityId: versionRow.entityId,
        data: ${currentSnapshot},
        createdBy: actorId
      })
${restoreM2M ? `\n${restoreM2M}\n` : ''}
      return tx.update(${ctx.tableVar})
        .set({ ...restoreData, updatedAt: now, updatedBy: actorId })
        .where(eq(${ctx.tableVar}.id, versionRow.entityId))
        .returning()
    })

    if (result.length === 0) throw new Error('${ctx.Singular} not found')

    ${ctx.cacheInvalidationFn}(${ctx.camelPlural}CacheTags.all)
    ${ctx.cacheInvalidationFn}(${ctx.camelPlural}CacheTags.byId(versionRow.entityId))
    ${ctx.cacheInvalidationFn}(entityVersionsCacheTags.for('${ctx.plural}', versionRow.entityId))
    const restored${ctx.Singular} = await get${ctx.Singular}ById(versionRow.entityId)

    return {
      success: true,
      ${ctx.camelSingular}: restored${ctx.Singular} ?? (result[0] as unknown as ${ctx.Singular})
    }
  } catch (error) {
    console.error('Error restoring ${ctx.singular} version:', error)
    throw new Error(error instanceof Error ? error.message : 'Failed to restore ${ctx.singular} version')
  }
}
`
}

// ============================================================================
// get-{singular}-select-options.ts / create-{singular}-select-option.ts
// ============================================================================

function buildSelectOptionFieldNames(ctx: SelectOptionCtx): string {
  return ctx.creatableSelectFields.map((field) => JSON.stringify(field.name)).join(', ')
}

function buildSelectOptionStaticOptions(ctx: SelectOptionCtx): string {
  const entries = ctx.creatableSelectFields
    .map((field) => {
      const options = (field.options ?? [])
        .map(
          (option) =>
            `{ label: ${JSON.stringify(option.label)}, value: ${JSON.stringify(option.value)} }`
        )
        .join(', ')
      return `  ${JSON.stringify(field.name)}: [${options}]`
    })
    .join(',\n')

  return `const STATIC_SELECT_OPTIONS = {\n${entries}\n} as const`
}

export function genGetSelectOptionsContent(ctx: SelectOptionCtx): string {
  return `'use server'

import db from '@admin/db'
import { ${ctx.selectOptionsTableVar} } from '@admin/db/schema'
import { asc, eq } from 'drizzle-orm'
${ctx.cacheReadImport}
import { ${ctx.camelSingular}SelectOptionsCacheTags } from './types'
import type { ${ctx.Singular}SelectOption, ${ctx.Singular}SelectOptionFieldName } from './types'

const SELECT_OPTION_FIELDS = [${buildSelectOptionFieldNames(ctx)}] as const

function isSelectOptionFieldName(fieldName: string): fieldName is ${ctx.Singular}SelectOptionFieldName {
  return (SELECT_OPTION_FIELDS as readonly string[]).includes(fieldName)
}

export async function get${ctx.Singular}SelectOptions(
  fieldName: ${ctx.Singular}SelectOptionFieldName
): Promise<${ctx.Singular}SelectOption[]> {
  'use cache'${ctx.cacheLifeLine}
  cacheTag(${ctx.camelSingular}SelectOptionsCacheTags.byField(fieldName))

  if (!isSelectOptionFieldName(fieldName)) return []

  try {
    const rows = await db
      .select()
      .from(${ctx.selectOptionsTableVar})
      .where(eq(${ctx.selectOptionsTableVar}.fieldName, fieldName))
      .orderBy(asc(${ctx.selectOptionsTableVar}.label))

    return rows as ${ctx.Singular}SelectOption[]
  } catch (error) {
    console.error('Error fetching ${ctx.singular} select options:', error)
    return []
  }
}
`
}

export function genCreateSelectOptionContent(ctx: SelectOptionCtx): string {
  return `'use server'

import { requireRole, UserRole } from '@admin/auth/middleware'
import db from '@admin/db'
import { ${ctx.selectOptionsTableVar} } from '@admin/db/schema'
import { eq } from 'drizzle-orm'
import { ${ctx.cacheInvalidationFn} } from 'next/cache'
import { ${ctx.camelSingular}SelectOptionsCacheTags } from './types'
import type {
  Create${ctx.Singular}SelectOptionInput,
  Create${ctx.Singular}SelectOptionResult,
  ${ctx.Singular}SelectOption,
  ${ctx.Singular}SelectOptionFieldName
} from './types'

${buildSelectOptionStaticOptions(ctx)}

function toSelectOptionValue(label: string): string {
  const normalized = label
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')

  return normalized || 'option'
}

async function resolveSelectOptionValue(
  fieldName: ${ctx.Singular}SelectOptionFieldName,
  label: string
): Promise<string> {
  const baseValue = toSelectOptionValue(label)
  const rows = await db
    .select({ value: ${ctx.selectOptionsTableVar}.value })
    .from(${ctx.selectOptionsTableVar})
    .where(eq(${ctx.selectOptionsTableVar}.fieldName, fieldName))
  const usedValues = new Set([
    ...STATIC_SELECT_OPTIONS[fieldName].map((option) => option.value),
    ...rows.map((row) => row.value)
  ])

  if (!usedValues.has(baseValue)) return baseValue

  let suffix = 2
  let value = \`\${baseValue}-\${suffix}\`
  while (usedValues.has(value)) {
    suffix += 1
    value = \`\${baseValue}-\${suffix}\`
  }

  return value
}

export async function create${ctx.Singular}SelectOption(
  input: Create${ctx.Singular}SelectOptionInput
): Promise<Create${ctx.Singular}SelectOptionResult> {
  const actor = await requireRole([UserRole.ADMIN, UserRole.EDITOR])

  try {
    const label = input.label.trim()
    if (!label) {
      return { success: false, error: 'Option label is required' }
    }

    const staticMatch = STATIC_SELECT_OPTIONS[input.fieldName].find(
      (option) => option.label.toLowerCase() === label.toLowerCase()
    )
    if (staticMatch) {
      return {
        success: true,
        option: {
          id: staticMatch.value,
          fieldName: input.fieldName,
          label: staticMatch.label,
          value: staticMatch.value,
          createdAt: '',
          updatedAt: ''
        }
      }
    }

    const rows = await db
      .select()
      .from(${ctx.selectOptionsTableVar})
      .where(eq(${ctx.selectOptionsTableVar}.fieldName, input.fieldName))
    const existing = rows.find((row) => row.label.toLowerCase() === label.toLowerCase())
    if (existing) {
      return { success: true, option: existing as ${ctx.Singular}SelectOption }
    }

    const actorId = actor.id
    const now = new Date().toISOString()
    const value = await resolveSelectOptionValue(input.fieldName, label)
    const [option] = await db
      .insert(${ctx.selectOptionsTableVar})
      .values({
        fieldName: input.fieldName,
        label,
        value,
        createdAt: now,
        updatedAt: now,
        createdBy: actorId,
        updatedBy: actorId
      })
      .returning()

    ${ctx.cacheInvalidationFn}(${ctx.camelSingular}SelectOptionsCacheTags.byField(input.fieldName))

    return { success: true, option: option as ${ctx.Singular}SelectOption }
  } catch (error) {
    console.error('Error creating ${ctx.singular} select option:', error)
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to create option'
    }
  }
}
`
}

// ============================================================================
// delete-{singular}.ts
// ============================================================================

export function genDeleteContent(ctx: EntityCtx): string {
  const deleteContext = lifecycleContext(
    ctx.plural,
    `,\n      entityId: id,\n      changedFields: []`
  )

  return `'use server'

import db from '@admin/db'
import { requireRole, UserRole } from '@admin/auth/middleware'
import { ${ctx.tableVar} } from '@admin/db/schema'
import { eq } from 'drizzle-orm'
import { ${ctx.cacheInvalidationFn} } from 'next/cache'
import { deleteEntityVersions, entityVersionsCacheTags } from '@admin/actions/entity-versions'
import { ${ctx.camelPlural}CacheTags } from './types'
${lifecycleHookImport()}
import type { ${ctx.Singular}DeleteResult } from './types'

export async function delete${ctx.Singular}(id: string): Promise<${ctx.Singular}DeleteResult> {
  const actor = await requireRole([UserRole.ADMIN, UserRole.EDITOR])
  const now = new Date().toISOString()
  const auditRequestMetadata = await getAuditRequestMetadata()
  const lifecycleContext = ${deleteContext}
  ${beforeHook('beforeDelete', 'lifecycleContext')}

  try {
    await db.transaction(async (tx) => {
      await deleteEntityVersions(tx, '${ctx.plural}', [id])
      await tx.delete(${ctx.tableVar}).where(eq(${ctx.tableVar}.id, id))
    })
    ${ctx.cacheInvalidationFn}(${ctx.camelPlural}CacheTags.all)
    ${ctx.cacheInvalidationFn}(${ctx.camelPlural}CacheTags.byId(id))
    ${ctx.cacheInvalidationFn}(entityVersionsCacheTags.for('${ctx.plural}', id))
    ${afterHook('afterDelete', 'lifecycleContext')}
    return { success: true }
  } catch (error) {
    console.error('Error deleting ${ctx.singular}:', error)
    return { success: false, error: error instanceof Error ? error.message : 'Failed to delete ${ctx.singular}' }
  }
}
`
}

// ============================================================================
// delete-bulk-{plural}.ts
// ============================================================================

export function genDeleteBulkContent(ctx: EntityCtx): string {
  return `'use server'

import db from '@admin/db'
import { requireRole, UserRole } from '@admin/auth/middleware'
import { ${ctx.tableVar} } from '@admin/db/schema'
import { inArray } from 'drizzle-orm'
import { ${ctx.cacheInvalidationFn} } from 'next/cache'
import { deleteEntityVersions, entityVersionsCacheTags } from '@admin/actions/entity-versions'
import { ${ctx.camelPlural}CacheTags } from './types'
${lifecycleHookImport()}
import type { ${ctx.Singular}DeleteResult } from './types'

export async function deleteBulk${ctx.Plural}(ids: string[]): Promise<${ctx.Singular}DeleteResult> {
  const actor = await requireRole([UserRole.ADMIN, UserRole.EDITOR])
  if (ids.length === 0) return { success: false, error: 'No items selected for deletion' }

  const now = new Date().toISOString()
  const auditRequestMetadata = await getAuditRequestMetadata()
  const lifecycleContexts = ids.map((id) => ({
    user: actor,
    entityType: '${ctx.plural}',
    timestamp: now,
    sourceType: 'admin_ui' as const,
    ipAddress: auditRequestMetadata.ipAddress,
    userAgent: auditRequestMetadata.userAgent,
    entityId: id,
    changedFields: []
  }))

  for (const lifecycleContext of lifecycleContexts) {
    ${beforeHook('beforeDelete', 'lifecycleContext')}
  }

  try {
    await db.transaction(async (tx) => {
      await deleteEntityVersions(tx, '${ctx.plural}', ids)
      await tx.delete(${ctx.tableVar}).where(inArray(${ctx.tableVar}.id, ids))
    })
    ${ctx.cacheInvalidationFn}(${ctx.camelPlural}CacheTags.all)
    for (const id of ids) {
      ${ctx.cacheInvalidationFn}(${ctx.camelPlural}CacheTags.byId(id))
      ${ctx.cacheInvalidationFn}(entityVersionsCacheTags.for('${ctx.plural}', id))
    }
    for (const lifecycleContext of lifecycleContexts) {
      ${afterHook('afterDelete', 'lifecycleContext')}
    }
    return { success: true }
  } catch (error) {
    console.error('Error deleting ${ctx.plural}:', error)
    return { success: false, error: error instanceof Error ? error.message : 'Failed to delete ${ctx.plural}' }
  }
}
`
}

// ============================================================================
// bulk-update-{plural}-sort-order.ts
// ============================================================================

export function genBulkSortOrderContent(ctx: EntityCtx): string {
  return `'use server'

import db from '@admin/db'
import { requireRole, UserRole } from '@admin/auth/middleware'
import { ${ctx.tableVar} } from '@admin/db/schema'
import { asc, eq, notInArray } from 'drizzle-orm'
import { ${ctx.cacheInvalidationFn} } from 'next/cache'
import { ${ctx.camelPlural}CacheTags } from './types'

export async function bulkUpdate${ctx.Plural}SortOrder(
  updates: Array<{ id: string; sortOrder: number }>
): Promise<{ success: boolean; error?: string }> {
  await requireRole([UserRole.ADMIN, UserRole.EDITOR])

  try {
    if (updates.length === 0) return { success: true }

    // Sort the provided items by their new position
    const sorted = [...updates].sort((a, b) => a.sortOrder - b.sortOrder)
    const updatedIds = sorted.map((u) => u.id)

    // Fetch all items NOT in the update set, preserving their current order
    const remaining = await db
      .select({ id: ${ctx.tableVar}.id })
      .from(${ctx.tableVar})
      .where(updatedIds.length > 0 ? notInArray(${ctx.tableVar}.id, updatedIds) : undefined)
      .orderBy(asc(${ctx.tableVar}.sortOrder))

    // Merge: place updated items at their new positions, append remaining after
    // The updated items define positions 0..N-1 within the loaded window.
    // Remaining items (not loaded by the client) keep their relative order and
    // are appended after the last updated item's position.
    const maxUpdatedPosition = Math.max(...sorted.map((u) => u.sortOrder))
    const allIds: string[] = new Array(maxUpdatedPosition + 1 + remaining.length)

    // Place updated items at their requested positions
    for (const u of sorted) {
      allIds[u.sortOrder] = u.id
    }

    // Fill remaining items into empty slots, then append the rest
    let fillIndex = 0
    let remainingIndex = 0
    // First fill any gaps before maxUpdatedPosition
    while (fillIndex <= maxUpdatedPosition && remainingIndex < remaining.length) {
      if (allIds[fillIndex] === undefined) {
        allIds[fillIndex] = remaining[remainingIndex].id as string
        remainingIndex++
      }
      fillIndex++
    }
    // Append remaining items after the updated window
    let appendIndex = maxUpdatedPosition + 1
    while (remainingIndex < remaining.length) {
      allIds[appendIndex] = remaining[remainingIndex].id as string
      remainingIndex++
      appendIndex++
    }

    // Renumber all items gap-free
    await Promise.all(
      allIds.filter(Boolean).map((id, index) =>
        db.update(${ctx.tableVar}).set({ sortOrder: index }).where(eq(${ctx.tableVar}.id, id))
      )
    )

    ${ctx.cacheInvalidationFn}(${ctx.camelPlural}CacheTags.all)
    return { success: true }
  } catch (error) {
    console.error('Error bulk updating sort order:', error)
    return { success: false, error: error instanceof Error ? error.message : 'Failed to bulk update sort order' }
  }
}
`
}

// ============================================================================
// M2M files: get-{rel}-for-{singular}.ts and set-{rel}-for-{singular}.ts
// ============================================================================

export function genM2MFiles(ctx: EntityCtx): Array<{ name: string; content: string }> {
  const files: Array<{ name: string; content: string }> = []

  for (const f of ctx.m2mFields) {
    const rel = f.relationship || ''
    const RelPascal = toPascalCase(rel)
    const relSingular = singularize(rel)
    const junctionVar = toCamelCase(`${ctx.singular}${RelPascal}`)
    const entityIdCol = `${ctx.singular}Id`
    const relIdCol = `${relSingular}Id`
    const kebabRel = rel
      .replace(/([A-Z])/g, '-$1')
      .toLowerCase()
      .replace(/^-/, '')

    // get file
    files.push({
      name: `get-${kebabRel}-for-${ctx.singular}.ts`,
      content: `'use server'

import db from '@admin/db'
import { ${junctionVar} } from '@admin/db/schema'
import { eq } from 'drizzle-orm'
${ctx.cacheReadImport}
import { ${ctx.camelPlural}CacheTags } from './types'

export async function get${RelPascal}For${ctx.Singular}(${ctx.singular}Id: string): Promise<string[]> {
  'use cache'${ctx.cacheLifeLine}
  cacheTag(${ctx.camelPlural}CacheTags.byId(${ctx.singular}Id))
  cacheTag(${ctx.camelPlural}CacheTags.all)

  try {
    const results = await db
      .select({ ${relIdCol}: ${junctionVar}.${relIdCol} })
      .from(${junctionVar})
      .where(eq(${junctionVar}.${entityIdCol}, ${ctx.singular}Id))
    return results.map((r) => r.${relIdCol})
  } catch (error) {
    console.error('Error fetching ${rel} for ${ctx.singular}:', error)
    return []
  }
}
`
    })

    // set file
    files.push({
      name: `set-${kebabRel}-for-${ctx.singular}.ts`,
      content: `'use server'

import db from '@admin/db'
import { requireRole, UserRole } from '@admin/auth/middleware'
import { ${junctionVar}, ${ctx.tableVar} } from '@admin/db/schema'
import { eq } from 'drizzle-orm'
import { ${ctx.cacheInvalidationFn} } from 'next/cache'
import { createEntityVersion, entityVersionsCacheTags } from '@admin/actions/entity-versions'
import { ${ctx.camelPlural}CacheTags } from './types'
import {
  build${ctx.Singular}VersionSnapshotWithRelationships,
  has${ctx.Singular}VersionChanges
} from './helpers'

export async function set${RelPascal}For${ctx.Singular}(
  ${ctx.singular}Id: string,
  ${rel}Ids: string[]
): Promise<{ success: boolean; error?: string }> {
  const actor = await requireRole([UserRole.ADMIN, UserRole.EDITOR])

  try {
    await db.transaction(async (tx) => {
      const [previousRow] = await tx
        .select()
        .from(${ctx.tableVar})
        .where(eq(${ctx.tableVar}.id, ${ctx.singular}Id))
        .limit(1)

      if (!previousRow) {
        throw new Error('${ctx.Singular} not found')
      }

      const previousSnapshot = await build${ctx.Singular}VersionSnapshotWithRelationships(
        previousRow as Record<string, unknown>,
        tx
      )
      const nextSnapshot = { ...previousSnapshot, ${f.name}: ${rel}Ids }
      const shouldCreateVersion = has${ctx.Singular}VersionChanges(previousSnapshot, nextSnapshot)

      if (shouldCreateVersion) {
        await createEntityVersion(tx, {
          entityType: '${ctx.plural}',
          entityId: ${ctx.singular}Id,
          data: previousSnapshot,
          createdBy: actor.id
        })
      }

      await tx.delete(${junctionVar}).where(eq(${junctionVar}.${entityIdCol}, ${ctx.singular}Id))
      if (${rel}Ids.length > 0) {
        await tx.insert(${junctionVar}).values(
          ${rel}Ids.map((${relSingular}Id) => ({
            ${entityIdCol}: ${ctx.singular}Id,
            ${relIdCol}: ${relSingular}Id
          }))
        )
      }
    })
    ${ctx.cacheInvalidationFn}(${ctx.camelPlural}CacheTags.all)
    ${ctx.cacheInvalidationFn}(${ctx.camelPlural}CacheTags.byId(${ctx.singular}Id))
    ${ctx.cacheInvalidationFn}(entityVersionsCacheTags.for('${ctx.plural}', ${ctx.singular}Id))
    return { success: true }
  } catch (error) {
    console.error('Error setting ${rel} for ${ctx.singular}:', error)
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to set ${rel}'
    }
  }
}
`
    })
  }

  return files
}

// ============================================================================
// get-distinct-{plural}-{field}.ts files
// ============================================================================

export function genDistinctFiles(ctx: EntityCtx): Array<{ name: string; content: string }> {
  if (!ctx.hasFilters) return []

  return ctx.filters
    .filter((filter) => filter.source === 'distinct')
    .map((filter) => {
      const fieldPascal = toPascalCase(filter.key)
      const kebabField = filter.key
        .replace(/([A-Z])/g, '-$1')
        .toLowerCase()
        .replace(/^-/, '')
      const kebabPlural = ctx.plural
        .replace(/([A-Z])/g, '-$1')
        .toLowerCase()
        .replace(/^-/, '')

      return {
        name: `get-distinct-${kebabPlural}-${kebabField}.ts`,
        content: `'use server'

import db from '@admin/db'
import { ${ctx.tableVar} } from '@admin/db/schema'
import { asc, isNotNull } from 'drizzle-orm'
${ctx.cacheReadImport}
import { ${ctx.camelPlural}CacheTags } from './types'

export async function getDistinct${ctx.Plural}${fieldPascal}(): Promise<string[]> {
  'use cache'${ctx.cacheLifeLine}
  cacheTag(${ctx.camelPlural}CacheTags.all)

  try {
    const results = await db
      .selectDistinct({ value: ${ctx.tableVar}.${filter.field} })
      .from(${ctx.tableVar})
      .where(isNotNull(${ctx.tableVar}.${filter.field}))
      .orderBy(asc(${ctx.tableVar}.${filter.field}))
    return results.map((r) => String(r.value)).filter(Boolean)
  } catch (error) {
    console.error('Error fetching distinct ${ctx.plural} ${filter.field}:', error)
    return []
  }
}
`
      }
    })
}

// ============================================================================
// index.ts (barrel)
// ============================================================================

export function genBarrelContent(
  files: Array<{ name: string; content: string }>,
  ctx: EntityCtx
): string {
  const lines: string[] = []

  // Re-export types
  const typeNames: string[] = [
    `${ctx.Singular}`,
    `${ctx.Plural}Page`,
    `${ctx.Plural}Query`,
    `${ctx.Singular}CreateInput`,
    `${ctx.Singular}CreateResult`,
    `${ctx.Singular}UpdateInput`,
    `${ctx.Singular}UpdateResult`,
    `${ctx.Singular}DeleteResult`,
    ctx.hasCreatableSelectFields ? `${ctx.Singular}SelectOption` : null,
    ctx.hasCreatableSelectFields ? `${ctx.Singular}SelectOptionFieldName` : null,
    ctx.hasCreatableSelectFields ? `Create${ctx.Singular}SelectOptionInput` : null,
    ctx.hasCreatableSelectFields ? `Create${ctx.Singular}SelectOptionResult` : null
  ].filter(Boolean) as string[]
  lines.push(`export type { ${typeNames.join(', ')} } from './types'`)
  lines.push('')

  // Re-export action functions from each file (skip helper/internal modules)
  for (const file of files) {
    if (file.name === 'types.ts' || file.name === 'helpers.ts' || file.name === 'markdown.ts')
      continue
    // Extract exported function names from the content
    const exportMatches = file.content.matchAll(/export async function (\w+)/g)
    const fnNames = [...exportMatches].map((m) => m[1])
    if (fnNames.length > 0) {
      const modulePath = `./${file.name.replace(/\.ts$/, '')}`
      lines.push(`export { ${fnNames.join(', ')} } from '${modulePath}'`)
    }
  }

  lines.push('')
  return lines.join('\n')
}
